<template>
  <div class="past-editable-month-cashflow-view">
    <div class="traffic-light-line">
      <div class="labeled-line">
        <img v-if="canMoveToPastCashflow" class="right-triangle" v-on:click="nextBudgetMonth()" :src="require('@/assets/icons/right-triangle.svg')">
        <div class="label" :class="{ positive: isMonthBalancePositive, zero: isMonthBalanceEven, negative: isMonthBalanceNegative }">
          {{ fullMonthName }}
        </div>
        <div class="line" :class="{ positive: isMonthBalancePositive, zero: isMonthBalanceEven, negative: isMonthBalanceNegative }"></div>
      <img v-if="canMoveToFutureCashflow" class="left-triangle" v-on:click="prevBudgetMonth()" :src="require('@/assets/icons/left-triangle.svg')">
      </div>
    </div>
    <div class="past-cashflow-view-container">
      <closed-end-of-month/>
      <past-months-graph/>
      <cashflow-type-variable-expense :key="`${presentedBudgetDate}-variable-expense`"/>
      <cashflow-type-economy/>
      <div v-for="expenses in recurringExpenses" v-bind:key="expenses.expenseCategory">
        <cashflow-type-recurring :expenses=expenses :expenseCategory="expenses.expenseCategory"/>
      </div>
      <div v-for="subtree in trackingCategories" v-bind:key="subtree.categoryId">
        <cashflow-type-tracking-category :subtree="subtree" />
      </div>
      <cashflow-type-fixed-expense/>
      <cashflow-type-income/>
    </div>
    <div class="past-cashflow-view-container">
      <hr class="divider" v-if="this.hasExcluded" />
      <cashflow-type-excluded v-if="this.hasExcluded"/>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import Segment from '../../../../../../../Segment';
import ClosedEndOfMonth from './components/ClosedEndOfMonth.vue';
import PastMonthsGraph from './components/PastMonthsGraph.vue';
import CashflowTypeIncome from './components/types/CashflowTypeIncome.vue';
import CashflowTypeExcluded from './components/types/CashflowTypeExcluded.vue';
import CashflowTypeVariableExpense from './components/types/CashflowTypeVariableExpense.vue';
import CashflowTypeRecurring from './components/types/CashflowTypeRecurring.vue';
import CashflowTypeTrackingCategory from './components/types/CashflowTypeTrackingCategory.vue';
import CashflowTypeEconomy from './components/types/CashflowTypeEconomy.vue';
import CashflowTypeFixedExpense from './components/types/CashflowTypeFixedExpense.vue';
import cashflowViewConsts from '../../../../../../../constants/cashflow-view';
import CashflowHistory from '../../../../../../../store/utilities/cashflow-history';

export default {
  name: 'PastEditableMonthCashflowView',
  components: {
    ClosedEndOfMonth,
    PastMonthsGraph,
    CashflowTypeIncome,
    CashflowTypeExcluded,
    CashflowTypeVariableExpense,
    CashflowTypeEconomy,
    CashflowTypeRecurring,
    CashflowTypeFixedExpense,
    CashflowTypeTrackingCategory,
  },
  async created() {
    Segment.trackUserGot('PastEditableCashflowViewEntered', {
      month: this.fullMonthName,
    });
  },
  computed: {
    ...mapState('cashflowView', ['presentedBudgets', 'cashflowUITree', 'presentedBudgetDate', 'allowViewCurrentMonth', 'currentBudgetDate']),
    ...mapGetters('session', ['householdName']),
    ...mapGetters('cashflowView', ['fullMonthName', 'balance', 'hasExcluded']),
    isMonthBalancePositive() {
      return this.balance > 0;
    },
    isMonthBalanceEven() {
      return this.balance === 0;
    },
    isMonthBalanceNegative() {
      return this.balance < 0;
    },
    canMoveToPastCashflow() {
      const lastBudget = _.last(this.presentedBudgets);
      return lastBudget && this.presentedBudgetDate !== lastBudget.budgetDate;
    },
    canMoveToFutureCashflow() {
      return this.allowViewCurrentMonth ? true : !this.isNextMonthCurrent;
    },
    isNextMonthCurrent() {
      return CashflowHistory.getNextBudgetDate(this.presentedBudgetDate) === this.currentBudgetDate;
    },
    recurringExpenses() {
      return this.cashflowUITree[cashflowViewConsts.cashflowTypes.recurring];
    },
    trackingCategories() {
      return this.cashflowUITree[cashflowViewConsts.cashflowTypes.trackingCategory];
    },
  },
  methods: {
    ...mapActions('cashflowView', ['moveToNextBudgetMonth', 'moveToPrevBudgetMonth']),
    async nextBudgetMonth() {
      await this.moveToNextBudgetMonth();
    },
    async prevBudgetMonth() {
      await this.moveToPrevBudgetMonth();
    },
  },
};
</script>

<style lang="scss" scoped>
  @import '../../../../../../../../node_modules/base-ui/src/scss/riseup-colors';
  @import '../../../../../../../scss/mixins';

  .past-editable-month-cashflow-view {
    padding-bottom: 80px;

    @media (min-width: 430px) {
      padding-bottom: 20px;
    }

    .traffic-light-line {
      @include responsive-padding;
      width: 100%;
      text-align: center;
      margin-bottom: 11px;

      .triangle-placeholder {
        height: 14px;
        width: 14px;
        display: inline-block;
        background-color: $riseup-white;
      }

      .labeled-line {
        width: 100%;
        padding-top: 12px;
        position: relative;
        display: inline-block;

        .positive {
          background-color: $riseup_green;
        }

        .zero {
          background-color: $riseup_mustard;
        }

        .negative {
          background-color: $riseup_coral;
        }

        .label {
          height: 18px;
          line-height: 15px;
          font-size: 14px;
          padding: 2px 4px 2px 4px;
          display: inline-block;
          color: $riseup_white;
          font-weight: bold;
          position: absolute;
          right: 0;
          top: -3px;
        }

        .line {
          height: 3px;
        }

        .circle {
          position: absolute;
          left: 0;
          top: 5px;
          width: 16px;
          height: 16px;
          border-radius: 50%;
          display: inline-block;
        }

        .right-triangle {
          position: absolute;
          right: -20px;
          top: -4px;
        }

        .left-triangle {
          position: absolute;
          left: -20px;
          top: -4px;
        }
      }
    }

    .past-cashflow-view-container {
      @include responsive-padding;
      font-family: SimplerPro;
      text-align: right;
      width: 100%;
      margin: 0 auto;
    }

    .divider {
      color: $riseup_gray_2;
      opacity: 0.5;
      margin: 25px 0;
    }
  }
</style>
